import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { AxiosResponse } from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { useApplicationContext } from "../../application/ApplicationContext";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { ProgressSpinner } from "primereact/progressspinner";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { DocumentShort, ReceiveStatus } from "../../models/DocumentOfReceive";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { RoleType } from "../../models/Role";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { Utility } from "../../core/Utility";
import { activeUser } from "../../services/AuthService";
import { getAllProducts } from "../../services/ProductService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import {
  getAllDocuments,
  deleteOneDocument,
} from "../../services/NewVersionService";
import "./ProcessingProductInputsPage.scss";

export const ProcessingProductInputsPage = () => {
  const {
    //load and set global filters
    state,
    selectedDateFromSet,
    selectedDateToSet,
    selectedProductCategorySet,
    selectedProductSet,
  } = useApplicationContext();

  const { t } = useTranslation();
  const toast = useRef(null);
  const navigate = useNavigate();

  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = new Date(businessYear, 0, 1, 0, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31, 23, 59, 59);
  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    if (businessYear === today.getFullYear()) {
      // tekuća godina -> poslednjih 7 dana
      const from = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 7,
        0,
        0,
        1,
      );
      const to = today;
      return { from, to };
    }
    // druga poslovna godina -> 1.1. - 31.12. te godine
    const from = new Date(businessYear, 0, 1, 0, 0, 1);
    const to = new Date(businessYear, 11, 31, 23, 59, 59);
    return { from, to };
  };
  const defaultRange = getDefaultRangeForBusinessYear();
  const [dateFrom, setDateFrom] = useState<Date>(
    state.selectedDateFrom
      ? new Date(state.selectedDateFrom)
      : defaultRange.from,
  );
  const [dateTo, setDateTo] = useState<Date>(
    state.selectedDateTo ? new Date(state.selectedDateTo) : defaultRange.to,
  );

  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [productCategoriesApi, setProductCategoriesApi] = useState<
    ProductCategory[]
  >([]);
  const [selectedProductCategory, setSelectedProductCategory] =
    useState<ProductCategory>();
  const [selectedProduct, setSelectedProduct] = useState<Product>(
    {} as Product,
  );
  const [allMercantileInputDocuments, setAllMercantileInputDocuments] =
    useState<DocumentShort[]>([]);
  const [mercantileInputDocuments, setMercantileInputDocuments] = useState<
    DocumentShort[]
  >([]);
  const [nettoSum, setNettoSum] = useState<number>(0);
  const [filteredInputDocuments, setFilteredInputDocuments] = useState<
    DocumentShort[]
  >([]);

  const [deleteConfirmVisible, setDeleteConfirmVisible] =
    useState<boolean>(false);
  const [inputToDelete, setInputToDelete] = useState<string>("");

  const [processing, setProcessing] = useState(false);
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    finishedDocumentNumber: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    loadingCityName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    partnerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    netto: { value: null, matchMode: FilterMatchMode.CONTAINS },
    driverName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    vehicleNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookups = async () => {
    const [pResp, cResp]: [AxiosResponse, AxiosResponse] = await Promise.all([
      getAllProducts(),
      getAllProductCategories(),
    ]);

    [pResp, cResp].forEach(ensureSuccess);

    const pr = pResp?.data?.result ?? [];
    const cats = cResp?.data?.result ?? [];

    setProductsApi(pr);
    setProductCategoriesApi(cats);

    return { pr, cats };
  };

  useEffect(() => {
    const run = async () => {
      try {
        setProcessing(true);
        await refreshLookups();

        if (state.selectedProductCategory) {
          setSelectedProductCategory(state.selectedProductCategory);
        }
        if (state.selectedProduct) {
          setSelectedProduct(state.selectedProduct);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setProcessing(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
    selectedDateFromSet(from);
    selectedDateToSet(to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessYear]);

  const resetFilters = () => {
    //reset all other filters
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      finishedDocumentNumber: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      loadingCityName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      partnerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      netto: { value: null, matchMode: FilterMatchMode.CONTAINS },
      driverName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      vehicleNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
      status: { value: null, matchMode: FilterMatchMode.EQUALS },
    });
    setGlobalFilterValue("");
  };

  useEffect(() => {
    formatMercantileDocumentsData(allMercantileInputDocuments);
    resetFilters();
  }, [selectedProduct, selectedProductCategory]);

  const loadDocuments = async (from: Date, to: Date) => {
    const response: AxiosResponse = await getAllDocuments(
      true, // mercantile
      true, // inputs
      from,
      to,
      false,
      undefined,
    );

    const allDocuments: DocumentShort[] = response.data.result ?? [];

    // inclusive filter po datumu do (do kraja dana)
    const toMs = new Date(to).getTime() + 24 * 60 * 60 * 1000;
    const naturalDocuments = allDocuments.filter((item: any) => {
      if (item.isNatural !== true) return false;
      const ts = item.date ? new Date(item.date).getTime() : 0;
      return ts < toMs;
    });

    setAllMercantileInputDocuments(naturalDocuments);
    setFilteredInputDocuments(naturalDocuments);
    formatMercantileDocumentsData(naturalDocuments);
    resetFilters();
  };

  // kad se promene datumi -> ponovo sa backend-a
  useEffect(() => {
    const run = async () => {
      try {
        setProcessing(true);
        await loadDocuments(dateFrom, dateTo);
      } catch (error) {
        console.error(error);
      } finally {
        setProcessing(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  const formatMercantileDocumentsData = (allDocuments: DocumentShort[]) => {
    let copyOfMercantileDocuments: DocumentShort[] = [];
    let amountOfNetto: number = 0;

    for (const mercantileInput of allDocuments) {
      //filtriranje prijemnica po magacinu, kategoriji i vrsti robe
      if (
        (selectedProductCategory?.id
          ? mercantileInput.categoryId == selectedProductCategory.id
          : true) && //prijemnice za izabranu grupu robe (default za sve)...
        (selectedProduct?.id
          ? mercantileInput.productId == selectedProduct.id
          : true) //prijemnice za izabranu robu (default za sve)
      ) {
        copyOfMercantileDocuments.push(mercantileInput);
        if (mercantileInput.netto) amountOfNetto += mercantileInput.netto;
      }
    }
    copyOfMercantileDocuments.sort((a: DocumentShort, b: DocumentShort) => {
      return a.date < b.date ? 1 : -1;
    });
    setMercantileInputDocuments(copyOfMercantileDocuments);
    setNettoSum(amountOfNetto);
  };

  useEffect(() => {
    let amountOfNetto: number = 0;
    for (const oneInput of filteredInputDocuments) {
      if (oneInput.netto) amountOfNetto += oneInput.netto;
    }
    setNettoSum(amountOfNetto);
  }, [filteredInputDocuments]);

  const onDateFromChange = (property: string, value: Date | null) => {
    setDateFrom(value as Date);
    selectedDateFromSet(value as Date);
  };

  const onDateToChange = (property: string, value: Date | null) => {
    setDateTo(value as Date);
    selectedDateToSet(value as Date);
  };

  const handleProductCategoryChange = (prop: string, value: any) => {
    const copyOfNewProductCategory = productCategoriesApi.find(
      (item) => item.id == value,
    ) as ProductCategory;
    setSelectedProductCategory(copyOfNewProductCategory);
    selectedProductCategorySet(copyOfNewProductCategory);
    setSelectedProduct({} as Product);
    selectedProductSet({} as Product);
  };

  const handleProductChange = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(selectedProduct);
    copyOfNewProduct = productsApi.find((item) => item.id == value) as Product;
    setSelectedProduct(copyOfNewProduct);
    selectedProductSet(copyOfNewProduct);
  };

  const onGlobalFilterChange = (e: any) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const statusOptions = [
    { label: t("Stigao"), value: ReceiveStatus.Arrived },
    { label: t("Predao robu"), value: ReceiveStatus.Finished },
    { label: t("Najavljen"), value: ReceiveStatus.NotArrived },
  ];

  const statusItemTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option.label}</span>
      </div>
    );
  };

  const statusRowFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={statusOptions}
        onChange={(e) => {
          options.filterApplyCallback(e.value);
        }}
        itemTemplate={statusItemTemplate}
        placeholder="Izaberite"
        className="p-column-filter"
        showClear
      />
    );
  };

  const accept = async () => {
    try {
      setProcessing(true);
      await deleteOneDocument(inputToDelete);
      setInputToDelete("");
      setDeleteConfirmVisible(false);
      await loadDocuments(dateFrom, dateTo);
      //@ts-ignore
      toast.current?.show({
        severity: "success",
        detail: t(`Prijemnica je obrisana`),
        life: 3000,
      });
    } catch (e) {
      console.error(e);
      // @ts-ignore
      toast.current?.show({
        severity: "error",
        detail: t("Greška pri brisanju"),
        life: 3000,
      });
    } finally {
      setProcessing(false);
    }
  };

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "all",
  });

  const renderHeader = () => {
    return (
      <div className="search-container">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Globalna pretraga..."
          />
        </span>
      </div>
    );
  };

  const formatFinishedDocumentId = (rowData: DocumentShort) => {
    return (
      rowData?.finishedDocumentNumber ?? rowData?.finishedDocumentId ?? "/"
    );
  };

  const formatProductName = (rowData: DocumentShort) => {
    return productsApi?.find((oneProduct) => oneProduct.id == rowData.productId)
      ?.name;
  };

  const formatDate = (rowData: DocumentShort) => {
    return Utility.formatDate(new Date(rowData.date));
  };

  const formatStatus = (rowData: DocumentShort) => {
    const status = rowData.status;
    const isFinished = rowData.finishedDocument;
    if (status === ReceiveStatus.Arrived) {
      return <span style={{ color: "#EDBD12" }}>{t("Stigao")}</span>;
    }
    if (status === ReceiveStatus.Finished) {
      if (isFinished !== true) {
        return <span style={{ color: "#00A553" }}>{t("Predao robu")}</span>;
      } else {
        //za jednom zakljucenu pa ponovo otkljucanu prijemnicu
        return <span style={{ color: "#DC3545" }}>{t("Predao robu")}</span>;
      }
    }
    return <span style={{ color: "#DC3545" }}>{t("Najavljen")}</span>;
  };

  const amountNetto = (rowData: DocumentShort) => {
    return rowData?.netto ? Utility.addThousandSeparator(rowData.netto) : "";
  };

  const isProcessingWarehouseman =
    activeUser?.role?.name === RoleType.Warehouseman &&
    activeUser?.warehouse?.finishingCenter === true;

  const formatOptions = (rowData: DocumentShort) => {
    return (
      <div className="action-buttons">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-eye"
          onButtonClick={() => {
            const id = rowData.id;
            navigate(`/document-of-receive-preview-fast/${id}`);
          }}
        />
        {(activeUser?.role?.name === RoleType.Superadmin ||
          activeUser?.role?.name === RoleType.Administrator ||
          activeUser?.role?.name === RoleType.FinishingCenter ||
          activeUser?.role?.name === RoleType.Director ||
          isProcessingWarehouseman) && (
          <ButtonInput
            addStyle={{ border: "none", background: "#198754" }}
            labelText={""}
            icon="bi bi-pencil"
            onButtonClick={() => {
              const id = rowData.id;
              navigate(`/document-of-receive-fast/${id}`);
            }}
            disabled={activeUser?.role?.name === RoleType.Director}
          />
        )}
        {(activeUser?.role?.name === RoleType.Superadmin ||
          activeUser?.role?.name === RoleType.Director) && (
          <ButtonInput
            addStyle={{ border: "none", background: "#198754" }}
            labelText={""}
            icon="bi bi-trash"
            onButtonClick={async () => {
              const id = rowData.id;
              setInputToDelete(id);
              setDeleteConfirmVisible(true);
            }}
            disabled={activeUser?.role?.name === RoleType.Director}
          />
        )}
      </div>
    );
  };

  const exportToExcel = () => {
    // Sortiranje po datumu (najstariji prvi) i po vremenu ako je isti datum
    const sortedDocuments = [...filteredInputDocuments].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      // Ako su datumi različiti, sortiraj po datumu
      if (dateA !== dateB) {
        return dateA - dateB; // Od najstarijeg ka najnovijem
      }

      // Ako su datumi isti, sortiraj po vremenu
      const timeA = a.time ? new Date(a.time).getTime() : 0;
      const timeB = b.time ? new Date(b.time).getTime() : 0;
      return timeA - timeB; // Od ranijeg ka kasnijem
    });

    const excelMercantileObject = sortedDocuments.map((item: DocumentShort) => {
      return {
        BrojDokumenta: item.finishedDocumentNumber ?? item.finishedDocumentId,
        Datum: Utility.formatDate(new Date(item.date as Date)),
        Vreme: Utility.formatTime(new Date(item.time as Date)),
        MestoUtovara: item.loadingCityName,
        MestoIstovara: item.warehouseName,
        BrojCelije: item.cellNumberFloor,
        Dobavljac: item.partnerName,
        Roba: item.productName,
        Vozac: item.driverName,
        BrojRegistracije: item.vehicleNumber,
        Bruto: item.brutto,
        Tara: item.tara,
        Neto: item.netto,
        UpucenaKolicina: item.sentQuantity,
        BrojOtpremnice: item.documentPairId,
        Vlaga: parseFloat((item.moisture ?? 0)?.toString()),
        Primese: parseFloat((item.impurity ?? 0)?.toString()),
        Defekt: parseFloat((item.defect ?? 0)?.toString()),
        Lom: parseFloat((item.breakage ?? 0)?.toString()),
        Hektolitar: parseFloat((item.hectoliter ?? 0)?.toString()),
        Protein: parseFloat((item.protein ?? 0)?.toString()),
      };
    });
    const excelObject = [
      {
        BrojDokumenta: "",
        Datum: "",
        Vreme: "",
        MestoUtovara: "",
        MestoIstovara: "",
        BrojCelije: "",
        Dobavljac: "",
        Roba: "",
        Vozac: "",
        BrojRegistracije: "",
        Bruto: "",
        Tara: "",
        Neto: "",
        UpucenaKolicina: "",
        BrojOtpremnice: "",
        Vlaga: "",
        Primese: "",
        Defekt: "",
        Lom: "",
        Hektolitar: "",
        Protein: "",
      },
      ...excelMercantileObject,
    ];
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelObject);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(
        excelBuffer,
        `Prijem naturalne robe ${Utility.formatDate(new Date())}`,
      );
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: any) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-boxes header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-left-title">
              {t("Prijemnice naturalne robe")}
            </span>
            <span className="header-elements-text">{`${t("od")}`}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements-text">{`${t("do")}`}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements-text">{`${t("za")}`}</span>
            <DropdownInput
              id="productCategory"
              value={selectedProductCategory?.id}
              onValueChange={handleProductCategoryChange}
              optionValue="id"
              optionLabel="name"
              options={productCategoriesApi.filter(
                (item) => item.type == ProductType.MercantileGoods,
              )}
              filter={false}
              labelText=""
              placeholderText={t("vrstu robe")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={processing}
            />
            <span className="header-elements"> </span>
            <DropdownInput
              id="selectedProduct"
              value={selectedProduct?.id}
              onValueChange={handleProductChange}
              optionValue="id"
              optionLabel="name"
              options={productsApi.filter(
                (item) => item.category?.id === selectedProductCategory?.id,
              )}
              filter={false}
              labelText=""
              placeholderText={t("Ime")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={selectedProductCategory === undefined}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 0 0 1rem",
              }}
              labelText={"reset"}
              icon="bi bi-arrow-repeat"
              onButtonClick={() => {
                const { from, to } = getDefaultRangeForBusinessYear();
                // reset lokalnih stanja
                setDateFrom(from);
                setDateTo(to);
                setSelectedProductCategory(undefined);
                setSelectedProduct({} as Product);
                // reset globalnih filtera
                selectedDateFromSet(from);
                selectedDateToSet(to);
                selectedProductCategorySet(undefined);
                selectedProductSet(undefined);
              }}
              disabled={processing}
            />
          </div>
          <div className="header-processing-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                marginLeft: "5px",
              }}
              labelText={""}
              icon="bi bi-file-earmark-excel"
              onButtonClick={exportToExcel}
              disabled={processing === true}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              scrollable={true}
              scrollHeight="80vh"
              sortMode="single"
              removableSort
              filters={filters}
              filterDisplay="row"
              globalFilterFields={[
                "finishedDocumentNumber",
                "documentPairId",
                "loadingCityName",
                "partnerName",
                "driverName",
                "vehicleNumber",
                "netto",
              ]}
              header={renderHeader()}
              emptyMessage="Nema prijemnica naturalne robe za zadate kriterijume"
              value={mercantileInputDocuments}
              onValueChange={(filteredData) =>
                setFilteredInputDocuments(filteredData)
              }
              showGridlines
              stripedRows
            >
              <Column
                frozen
                body={formatFinishedDocumentId}
                field="finishedDocumentNumber"
                filterField={"finishedDocumentNumber"}
                filter
                header={t("Br.Dok.")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatStatus}
                filter
                filterField={"status"}
                showFilterMenu={false}
                filterElement={statusRowFilterTemplate}
                style={{ minWidth: "150px" }}
                header={t("Status")}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatDate}
                header={t("Datum")}
                style={{ minWidth: "80px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="loadingCityName"
                filter
                header={t("Mesto utovara")}
                style={{ minWidth: "90px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="warehouseName"
                header={t("Mesto istovara")}
                style={{ minWidth: "90px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="partnerName"
                sortable
                filter
                header={t("Dobavljač")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatProductName}
                header={t("Naziv robe")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="driverName"
                filter
                header={t("Vozač")}
                style={{ minWidth: "80px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="vehicleNumber"
                filter
                header={t("Broj registracije")}
                style={{ minWidth: "80px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={amountNetto}
                filterField={"netto"}
                style={{ minWidth: "80px" }}
                filter
                header={t("Količina")}
                footer={Utility.addThousandSeparator(nettoSum) + " kg"}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatOptions}
                header={t("Opcije")}
                style={{ minWidth: "150px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
            </DataTable>
          </div>
        )}
      </div>
      <ConfirmDialog
        visible={deleteConfirmVisible}
        onHide={() => setDeleteConfirmVisible(false)}
        message={t("Da li ste sigurni da želite da obrišete prijemnicu?")}
        header={t("Potvrda brisanja prijemnice")}
        icon="bi bi-exclamation-circle"
        acceptLabel={`${t("Da")}`}
        acceptClassName="accept-button"
        accept={accept}
        rejectLabel={`${t("Ne")}`}
        rejectClassName="reject-button"
        reject={() => {}}
      />
    </PageLayout>
  );
};
